import "./list-package-accommodation.scss";

import { GetPackageAccommodationModel } from "@factory/model/query-model-package-accommodation";
import { useQyGetPackageAccommodation } from "@factory/query/package-accommodation.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { useDateTimeContext } from "../../../shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "../../../shared/utility/formats/currency-format";
import { FilterPackageAccommodation } from "../filter-package-accommodation/filter-package-accommodation";
import { useFilterPackageAccommodationContext } from "../filter-package-accommodation/filter-package-accommodation.context";

export function ListPackageAccommodation() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { formatDate } = useDateTimeContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterPackageAccommodationContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: packageAccommodationResponse,
    isLoading,
    isError,
    error,
  } = useQyGetPackageAccommodation(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetPackageAccommodationModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterPackageAccommodation />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{packageAccommodationResponse?.count}</b>
      </h5>

      <DataTable
        value={packageAccommodationResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="hotel_name" header="Hotel" sortable></Column>
        <Column field="room_type_name" header="Room_type" sortable></Column>
        <Column field="num_people" header="Num_people" sortable></Column>
        <Column field="unit" header="Unit" sortable></Column>
        <Column
          field="valid_from"
          header="Valid_from"
          sortable
          body={(item) => <p>{formatDate(item.valid_from)}</p>}
        ></Column>
        <Column
          field="valid_until"
          header="Valid_until"
          sortable
          body={(item) => <p>{formatDate(item.valid_until)}</p>}
        ></Column>
        <Column
          field="price"
          header="Price"
          sortable
          body={(item) => <p>{currencyFormat(item.price)}</p>}
        ></Column>
        <Column field="description" header="Description" sortable></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={packageAccommodationResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListPackageAccommodation" className="package-accommodation">
      <HeaderContent title={<h1>Package Accommodation</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListPackageAccommodation;
